import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { toast } from 'sonner';
import { Plus, MapPin, Calendar, User, LogOut, Trash2, Users, ShieldAlert } from 'lucide-react';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Dashboard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, setUser } = React.useContext(AuthContext);

  const isVerified = user?.is_verified || user?.is_admin;

  useEffect(() => {
    fetchPosts();
  }, [user]);

  const handleDeletePost = async (postId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully');
      fetchPosts(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
    toast.success('Logged out successfully');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div 
              style={{ backgroundColor: 'rgb(26, 160, 157)' }}
              className="px-6 py-2.5 rounded-lg shadow-md"
            >
              <h1 className="text-xl font-bold text-white logo-text whitespace-nowrap">
                STRAY SPOTTER
              </h1>
            </div>
            <div className="flex gap-3 items-center ml-6">
              <span className="text-sm text-gray-600 hidden sm:inline">
                Welcome, {user?.name}
                {user?.is_admin && <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">ADMIN</span>}
              </span>
              <Button
                onClick={() => navigate('/create')}
                data-testid="create-post-btn"
                style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                className="hover:opacity-90 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Report Animal
              </Button>
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                data-testid="profile-btn"
                className="hidden sm:flex"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
              {user?.is_admin && (
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  data-testid="admin-panel-btn"
                  className="hidden sm:flex border-purple-200 text-purple-600 hover:bg-purple-50"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Admin Panel
                </Button>
              )}
              <Button
                onClick={handleLogout}
                variant="outline"
                data-testid="logout-btn"
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Map View */}
        <div className="mb-8">
          <Card className="overflow-hidden shadow-lg border-0">
            {isVerified ? (
              <div className="h-96" data-testid="map-container">
                <MapContainer
                  center={[51.505, -0.09]}
                  zoom={2}
                  className="h-full w-full"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {posts.map(post => (
                    post.location && (
                      <Marker
                        key={post.id}
                        position={[post.location.lat, post.location.lng]}
                      >
                        <Popup>
                          <div className="p-2">
                            <h3 className="font-bold text-sm mb-1">{post.title}</h3>
                            <p className="text-xs text-gray-600 mb-2">{post.description.substring(0, 100)}...</p>
                            <button
                              onClick={() => navigate(`/post/${post.id}`)}
                              style={{ color: 'rgb(26, 160, 157)' }}
                              className="text-xs hover:opacity-80 font-medium"
                            >
                              View Details &rarr;
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    )
                  ))}
                </MapContainer>
              </div>
            ) : (
              <div className="h-96 bg-gray-100 flex items-center justify-center" data-testid="map-masked">
                <div className="text-center p-6">
                  <ShieldAlert className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                  <h3 className="font-bold text-lg text-gray-800 mb-2">Map Locations Hidden</h3>
                  <p className="text-sm text-gray-600 mb-4">Verify your identity to see animal locations on the map.</p>
                  <Button onClick={() => navigate('/profile')} className="text-white" style={{ backgroundColor: 'rgb(26, 160, 157)' }}>
                    Verify My Identity
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Posts Grid */}
        <div>
          <h2 className="text-3xl font-bold mb-6 text-gray-900" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Recent Reports</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'rgb(26, 160, 157)' }}></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No animals reported yet</p>
              <Button
                onClick={() => navigate('/create')}
                style={{ backgroundColor: 'rgb(26, 160, 157)' }}
                className="hover:opacity-90 text-white"
              >
                Report the First One
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="posts-grid">
              {posts.map(post => (
                <Card
                  key={post.id}
                  data-testid={`post-card-${post.id}`}
                  className="overflow-hidden hover:shadow-xl transform hover:-translate-y-1 transition-all cursor-pointer border-0 shadow-lg"
                  onClick={() => navigate(`/post/${post.id}`)}
                >
                  {post.images && post.images.length > 0 && (
                    <div className="h-48 overflow-hidden bg-gray-100 relative">
                      {(post.images[0].includes('placeholder.com') || post.images[0].includes('placehold.co')) && post.images[0].includes('STRAY+SPOTTER') ? (
                        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'rgb(26, 160, 157)' }}>
                          <h3 className="text-3xl font-bold text-white logo-text">STRAY SPOTTER</h3>
                        </div>
                      ) : (
                        <img
                          src={post.images[0]}
                          alt={post.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      )}
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-bold text-lg text-gray-900 flex-1">{post.title}</h3>
                      {user?.is_admin && (
                        <Button
                          onClick={(e) => handleDeletePost(post.id, e)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50 ml-2"
                          data-testid={`admin-delete-${post.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{post.description}</p>
                    
                    <div className="flex flex-col gap-2 text-xs text-gray-500">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                        <span className="line-clamp-1">{isVerified ? (post.location?.address || 'Location unknown') : 'Location hidden - verify to see'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                        <span>By {post.user_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" style={{ color: 'rgb(26, 160, 157)' }} />
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
